import React from "react";

const DEFAULT_CATEGORY_COLORS = {
    EMDR: "#6B7F9E",
    Grounding: "#8A9068",
    ACT: "#9B7D5A",
    DBT: "#BFA3B2",
    "General Wellness": "#4A7B79",
};

/**
 * Card used on the public `/resources` page and — when `palette` is
 * supplied — on `/alchemists-table`. Renders a direct Download button
 * linked to `r.download_url` (or a "Coming soon" placeholder when no
 * file has been uploaded).
 *
 * `palette` (optional) tunes the accents so the same card can live in
 * either the Resources (oxblood/gold) or Alchemist Table (dusty
 * blue/warm blush) aesthetic without duplicating markup.
 */
export default function ResourceCard({
    r,
    palette,
}) {
    const p = palette || {
        surface: "#FAFAF8",
        border: "#E2DDD8",
        thumbBg: "#F2EDE6",
        title: "#2C2825",
        body: "rgba(44,40,37,0.72)",
        placeholderText: "#9B7D5A",
        buttonBg: "#2C2825",
        buttonText: "#FAFAF8",
        comingSoonText: "#9B7D5A",
        comingSoonBorder: "#E2DDD8",
        categoryColors: DEFAULT_CATEGORY_COLORS,
        fallbackTag: "#9B7D5A",
    };

    const tagColor = (p.categoryColors && p.categoryColors[r.category]) || p.fallbackTag;

    return (
        <article
            data-testid={`resource-card-${r.slug}`}
            className="flex flex-col group transition-transform duration-500 hover:-translate-y-1"
            style={{ background: p.surface, border: `1px solid ${p.border}` }}
        >
            <div className="relative aspect-[4/3] overflow-hidden" style={{ background: p.thumbBg }}>
                {r.thumbnail_url ? (
                    <img
                        src={r.thumbnail_url}
                        alt={r.title}
                        className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                        loading="lazy"
                    />
                ) : (
                    <div
                        className="w-full h-full flex items-center justify-center font-serif text-3xl italic"
                        style={{ color: p.placeholderText }}
                    >
                        {r.title.split(" ").slice(0, 2).join(" ")}
                    </div>
                )}
            </div>
            <div className="p-8 flex flex-col flex-1">
                {r.category && (
                    <span
                        className="mb-4 text-[10px] tracking-[0.32em] uppercase font-medium"
                        style={{ color: tagColor, fontFamily: "Figtree" }}
                        data-testid={`resource-category-${r.slug}`}
                    >
                        {r.category}
                    </span>
                )}
                <h3
                    className="font-serif text-2xl sm:text-3xl leading-tight mb-4"
                    style={{ color: p.title }}
                >
                    {r.title}
                </h3>
                <p
                    className="leading-[1.85] text-base mb-8 flex-1"
                    style={{ color: p.body }}
                >
                    {r.description}
                </p>

                {r.download_url ? (
                    <a
                        href={r.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`resource-download-${r.slug}`}
                        className="mt-auto inline-flex items-center justify-center gap-2 transition-opacity duration-300 hover:opacity-90"
                        style={{
                            background: p.buttonBg,
                            color: p.buttonText,
                            padding: "16px 20px",
                            fontFamily: "Figtree",
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            fontSize: "11px",
                            fontWeight: 500,
                        }}
                    >
                        Download <span aria-hidden>↓</span>
                    </a>
                ) : (
                    <span
                        data-testid={`resource-coming-soon-${r.slug}`}
                        className="mt-auto inline-flex items-center justify-center text-[11px] tracking-[0.28em] uppercase"
                        style={{
                            color: p.comingSoonText,
                            padding: "16px 20px",
                            border: `1px solid ${p.comingSoonBorder}`,
                        }}
                    >
                        Coming soon
                    </span>
                )}
            </div>
        </article>
    );
}
